import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { BannerPositionsDataService, BannerPositionsColumnsService } from '../../services';
import { DialogBannerPositionFormComponent } from './banner-position-form/banner-position-form.component';
import { DialogBannerPositionDetailsComponent } from './banner-position-details/banner-position-details.component';
import { BannerPosition, BannerPositionsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-banner-positions',
  templateUrl: './banner-positions.component.html',
  styleUrls: ['./banner-positions.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class BannerPositionsComponent implements OnInit, OnDestroy {

  private bannerPositionAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private bannerPositionEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private bannerPositionDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public bannerPositionToDelete: BannerPosition = {};
  public bannerPositions: BannerPosition[] = [];
  public bannerPosition: BannerPosition = {};
  public selectedBannerPositions: BannerPosition[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private bannerPositionsDataService: BannerPositionsDataService,
    private bannerPositionsColumnsService: BannerPositionsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('BannerPositionsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Banner Positions - Management');
    this.primengConfig.ripple = true;
    this.getBannerPositions();
  }

  ngOnDestroy(): void {
    console.log('BannerPositionsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getBannerPositions(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.bannerPositionsDataService.getBannerPositions(this.parameters).subscribe((response: BannerPositionsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.bannerPositions = response.results;
        } else {
          this.bannerPositions = this.bannerPositions.concat(response.results);
        }
        if (response.inlineCount > this.bannerPositions.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getBannerPositions();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('BANNERPOSITIONSNOTLOADED')});
    });
  }

  importBannerPositions(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.bannerPositionsDataService.addBannerPositions(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('BANNERPOSITIONSCREATED')});
                if (proceed === j) {
                  this.getBannerPositions();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('BANNERPOSITIONSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('BANNERPOSITIONSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.bannerPositionsDataService.updateBannerPositions(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('BANNERPOSITIONSUPDATED')});
                if (proceed === j) {
                  this.getBannerPositions();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('BANNERPOSITIONSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('BANNERPOSITIONSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.bannerPositionsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('bannerPositions.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedBannerPositions() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Banner Positions?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.bannerPositionsDataService.deleteBannerPositions(this.selectedBannerPositions).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Banner Positions Deleted', life: 3000});
            this.getBannerPositions();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Banner Positions not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Banner Positions not deleted'});
        });
        this.selectedBannerPositions = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.bannerPositions, this.columns, 'BannerPositions');
  }

  exportExcel() {
    ToolsService.exportExcel(this.bannerPositions, this.columns, 'BannerPositions');
  }

  exportCsv() {
    ToolsService.exportCsv(this.bannerPositions, this.columns, 'BannerPositions');
  }

  refresh() {
    this.bannerPositionsDataService.clearCache();
    this.getBannerPositions();
  }

  bannerPositionAdd(): void {
    if (!this.navigation.getParamValue('bannerPositions.addInDialog', true)) {
      this.router.navigate(['/banner-positions/add']);
      return;
    }
    this.bannerPositionAddDialog = this.dialogService.open(DialogBannerPositionFormComponent, {
      header: '', // this.translate.instant('NEWBANNERPOSITION'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        bannerPosition: {id: null},
        users: this.bannerPositionsColumnsService.users,
      },
    });

    this.bannerPositionAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'BannerPosition added'});
        this.getBannerPositions();
      }
    });
  }

  bannerPositionEdit(bannerPosition: BannerPosition): void {
    if (!this.navigation.getParamValue('bannerPositions.editInDialog', true)) {
      this.router.navigate(['/banner-positions/edit/' + bannerPosition.id]);
      return;
    }
    this.bannerPositionEditDialog = this.dialogService.open(DialogBannerPositionFormComponent, {
      header: '', // this.translate.instant('EDITBANNERPOSITION') + ' ' + bannerPosition.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        bannerPosition: bannerPosition,
        users: this.bannerPositionsColumnsService.users,
      },
    });

    this.bannerPositionEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'BannerPosition updated'});
        this.getBannerPositions();
      }
    });
  }

  bannerPositionDetails(bannerPosition: BannerPosition): void {
    if (!this.navigation.getParamValue('bannerPositions.detailsInDialog', true)) {
      this.router.navigate(['/banner-positions/details/' + bannerPosition.id]);
      return;
    }
    this.bannerPositionDetailsDialog = this.dialogService.open(DialogBannerPositionDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        bannerPosition: bannerPosition,
      }
    });
  }

  bannerPositionDelete(bannerPosition: BannerPosition): void {
    this.bannerPositionToDelete = bannerPosition;
    this.messageService.clear();
    this.messageService.add({
      key: 'bannerPositionDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + bannerPosition.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.bannerPositionsDataService.deleteBannerPosition(this.bannerPositionToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Banner Position deleted'});
      this.getBannerPositions();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Banner Position not deleted'});
    });
  }

  onDeleteReject(): void {
    this.bannerPositionToDelete = {};
    this.messageService.clear('bannerPositionDelete');
  }

}
